// Blueprint Spec Sheet form-config API. GET returns the saved config or null
// (caller falls back to DEFAULT_SPEC_SHEET_CONFIG).
export const fetchSpecSheetFormConfig = async () => {
  const res = await fetch("/api/sbcf/specsheet/formConfig");
  if (!res.ok) throw new Error("Failed to load form config");
  return res.json();
};

export const saveSpecSheetFormConfig = async (config, updatedBy) => {
  const res = await fetch("/api/sbcf/specsheet/formConfig", {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ config, updatedBy }),
  });
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Failed to save form config");
  return content;
};

export const resetSpecSheetFormConfig = async () => {
  const res = await fetch("/api/sbcf/specsheet/formConfig", { method: "DELETE" });
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Failed to reset form config");
  return content;
};

// ---- Submissions ----

const jsonRequest = async (url, options) => {
  const res = await fetch(url, {
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    ...options,
  });
  const content = await res.json();
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

export const submitSpecSheet = (payload) =>
  jsonRequest("/api/sbcf/specsheet/submissions", {
    method: "POST",
    body: JSON.stringify(payload),
  });

// mode: "assigner" (all) | "team" (assigned to user) | "sales" (submitted by user)
export const fetchSubmissions = (mode, userId) =>
  jsonRequest(
    `/api/sbcf/specsheet/submissions?mode=${encodeURIComponent(
      mode
    )}&userId=${encodeURIComponent(userId || "")}`
  );

export const fetchSubmission = (id) =>
  jsonRequest(`/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}`);

export const assignSubmission = (id, assignedTo) =>
  jsonRequest(`/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/assign`, {
    method: "PUT",
    body: JSON.stringify({ assignedTo }),
  });

export const updateSubmissionStatus = (id, status, actor) =>
  jsonRequest(`/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/status`, {
    method: "PUT",
    body: JSON.stringify({ status, actor }),
  });

export const fetchSubmissionChat = (id, channel) =>
  jsonRequest(
    `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/chat/${channel}`
  );

export const sendSubmissionChat = (id, channel, sender, message) =>
  jsonRequest(
    `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/chat/${channel}`,
    { method: "POST", body: JSON.stringify({ sender, message }) }
  );

export const reviseSubmission = (id, payload) =>
  jsonRequest(`/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/revise`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

const uploadFiles = async (url, files, uploadedBy) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  formData.append("uploadedBy", JSON.stringify(uploadedBy));
  const res = await fetch(url, { method: "POST", body: formData });
  let content = {};
  try {
    content = await res.json();
  } catch (e) {
    /* non-JSON error body */
  }
  if (!res.ok)
    throw new Error(content.error || `Failed to upload files (${res.status})`);
  return content;
};

// SBCF team files (Files card)
export const uploadSubmissionAttachments = (id, files, uploadedBy) =>
  uploadFiles(
    `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/attachments`,
    files,
    uploadedBy
  );

// Salesperson attachments uploaded with the form
export const uploadSalesAttachments = (id, files, uploadedBy) =>
  uploadFiles(
    `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/salesAttachments`,
    files,
    uploadedBy
  );

export const attachmentUrl = (id, attachmentId) =>
  `/api/sbcf/specsheet/submissions/${encodeURIComponent(
    id
  )}/attachments/${encodeURIComponent(attachmentId)}`;

export const deleteSubmissionAttachment = (id, attachmentId) =>
  jsonRequest(attachmentUrl(id, attachmentId), { method: "DELETE" });

export const requestSubmissionApproval = (id, requestedBy, note) =>
  jsonRequest(
    `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/approval/request`,
    { method: "POST", body: JSON.stringify({ requestedBy, note }) }
  );

export const respondSubmissionApproval = (id, respondedBy, approved, note) =>
  jsonRequest(
    `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/approval/respond`,
    { method: "POST", body: JSON.stringify({ respondedBy, approved, note }) }
  );

// ---- Personal templates ----

export const fetchTemplates = (userId) =>
  jsonRequest(
    `/api/sbcf/specsheet/templates?userId=${encodeURIComponent(userId || "")}`
  );

export const fetchTemplate = (id) =>
  jsonRequest(`/api/sbcf/specsheet/templates/${encodeURIComponent(id)}`);

export const saveTemplate = (payload) =>
  jsonRequest("/api/sbcf/specsheet/templates", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const deleteTemplate = (id, userId) =>
  jsonRequest(
    `/api/sbcf/specsheet/templates/${encodeURIComponent(
      id
    )}?userId=${encodeURIComponent(userId || "")}`,
    { method: "DELETE" }
  );

export const fetchAssignees = () => jsonRequest("/api/sbcf/specsheet/assignees");

export const submissionPdfUrl = (id) =>
  `/api/sbcf/specsheet/submissions/${encodeURIComponent(id)}/pdf`;
